/**
 * Signatures in the composer (DESIGN.md T11): the default signature for new messages or for replies/forwards goes
 * into the initial body (marked block, mail/signature.ts in @platform/ui), the picker in the composer footer
 * switches or removes it. A mandatory signature (admin) locks the picker. Drafts keep the signature they hold.
 */
import { useI18n } from '@platform/i18n';
import { signatureIdIn, useAsync, withSignature, type ComposerSignatureControls } from '@platform/ui';
import { useCallback } from 'react';
import { api, type MySignatures } from './api';
import type { ComposeMode, ComposeRequest } from './mail-context';

/** Which default applies: new messages, or replies and forwards; none for drafts (they hold their own). */
export function signatureKind(mode: ComposeMode): 'new' | 'reply' | undefined {
  if (mode === 'draft') return undefined;
  return mode === 'new' ? 'new' : 'reply';
}

/** The request with the default signature in its body, plus the picker controls (without labels). */
export function applySignature(request: ComposeRequest, signatures: MySignatures | undefined): { request: ComposeRequest; controls: Omit<ComposerSignatureControls, 'labels'> | undefined } {
  if (!signatures) return { request, controls: undefined };
  const kind = signatureKind(request.mode);
  const body = request.initial?.body;
  const options = signatures.signatures.map((s) => ({ id: s.id, name: s.name, html: s.html, central: s.source === 'central' }));
  if (body?.format === 'text') return { request, controls: undefined };
  if (kind === undefined) return { request, controls: { options, initialId: signatureIdIn(body?.content ?? '') } };
  const chosen = options.find((option) => option.id === signatures.defaults[kind]);
  const content = withSignature(body?.content ?? '', chosen);
  return {
    request: chosen ? { ...request, initial: { ...request.initial, body: { format: 'html', content } } } : request,
    controls: { options, initialId: chosen?.id, locked: signatures.locked[kind] },
  };
}

export interface ComposeSignatures {
  /** Request with the default signature and the composer's signature controls. */
  prepare: (request: ComposeRequest) => { request: ComposeRequest; controls: ComposerSignatureControls | undefined };
  /** The signature list has been answered (loaded or failed), so a composer can be prepared. */
  ready: boolean;
  /** Load again after the user changed signatures (settings page). */
  reload: () => void;
}

export function useComposeSignatures(enabled = true): ComposeSignatures {
  const { t } = useI18n();
  const signatures = useAsync(() => api.mySignatures(), 0, enabled);
  const { data } = signatures;
  const prepare = useCallback(
    (request: ComposeRequest) => {
      const { request: prepared, controls } = applySignature(request, data);
      const labels = {
        signature: t('webmail.compose.signature'),
        none: t('webmail.compose.noSignature'),
        central: t('signatures.central'),
        locked: t('webmail.compose.signatureLocked'),
      };
      return { request: prepared, controls: controls && { ...controls, labels } };
    },
    [data, t],
  );
  return { prepare, reload: signatures.reload, ready: signatures.data !== undefined || signatures.error !== undefined };
}
